/* ============================================================
   Salty Seats - 2026 product page behaviours.

   - Configurator: colour / pack / add-on state, live pricing
     from real Shopify variants, receipt, free-shipping bar and
     an AJAX add-to-cart that adds every line in one call.
   - Recline demo, FAQ accordions, bundle add buttons.

   All copy comes from section settings; nothing here is
   hard-coded for a particular product.
   ============================================================ */

(function () {
  'use strict';

  /* ---------------- money ---------------- */

  function formatMoney(cents, format) {
    var value = (cents || 0) / 100;
    var fmt = format || '${{amount}}';

    function withCommas(n, decimals) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    function withPeriods(n, decimals) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join(',');
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, token) {
      switch (token) {
        case 'amount_no_decimals':
          return withCommas(value, 0);
        case 'amount_with_comma_separator':
          return withPeriods(value, 2);
        case 'amount_no_decimals_with_comma_separator':
          return withPeriods(value, 0);
        default:
          return withCommas(value, 2);
      }
    });
  }

  /* ---------------- helpers ---------------- */

  function parseConfig(root) {
    var node = root.querySelector('[data-sspdp-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      /* Never fail silently - without the config the whole
         configurator is inert, so say why in the console. */
      if (window.console && console.warn) {
        console.warn('[ss-pdp] configurator config could not be parsed:', e);
      }
      return null;
    }
  }

  function businessDay(offset) {
    var d = new Date();
    var added = 0;
    while (added < offset) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return days[d.getDay()] + ' ' + d.getDate() + ' ' + months[d.getMonth()];
  }

  function norm(v) {
    return String(v == null ? '' : v).trim().toLowerCase();
  }

  function firstAvailableVariant(product) {
    var found = null;
    (product.variants || []).forEach(function (v) {
      if (!found && v.available) found = v;
    });
    return found;
  }

  /* Does this product actually sell by colour? True when any variant
     option matches one of the configured colour names. */
  function sellsByColour(product, colourNames) {
    if (!product || !product.variants) return false;
    var names = (colourNames || []).map(norm).filter(Boolean);
    var aware = false;
    product.variants.forEach(function (v) {
      (v.options || []).forEach(function (o) {
        if (names.indexOf(norm(o)) !== -1) aware = true;
      });
    });
    return aware;
  }

  /* The variant whose option values match `values` in order.
     Multi-seat packs are sold as one option per seat, so a two pack
     reads "Mocha / Sage" and a three pack "Apricot / Mocha / Sage". */
  function variantByValues(product, values) {
    var want = values.map(norm);
    var hit = null;
    (product.variants || []).forEach(function (v) {
      if (hit) return;
      var opts = (v.options || []).map(norm);
      if (opts.length !== want.length) return;
      var same = true;
      for (var i = 0; i < want.length; i++) {
        if (opts[i] !== want[i]) same = false;
      }
      if (same) hit = v;
    });
    return hit;
  }

  /* The variant where EVERY option is the chosen colour, e.g. the
     all-Mocha two pack. Picking any variant that merely contains
     "Mocha" would sell an Aqua/Mocha pair to someone who chose Mocha. */
  function uniformVariant(product, colourName) {
    var want = norm(colourName);
    if (!want) return null;
    var hit = null;
    (product.variants || []).forEach(function (v) {
      if (hit) return;
      var opts = (v.options || []).map(norm);
      if (!opts.length) return;
      var all = true;
      opts.forEach(function (o) {
        if (o !== want) all = false;
      });
      if (all) hit = v;
    });
    return hit;
  }

  /* Resolve a product to a variant for the chosen colour.

     Returns { variant, state } where state is one of:
       'matched'     - this exact colour, safe to name it on the page
       'colourless'  - product has no colour dimension, so the colour
                       must NOT be claimed in labels or the receipt
       'unavailable' - product sells by colour but not this one, so
                       nothing may be added

     Falling back to an arbitrary variant would ship e.g. a Mocha two
     pack to someone who chose Aqua, with every label still saying
     Aqua, so a miss returns nothing instead. */
  function resolveVariant(product, colourName, colourNames) {
    if (!product || !product.variants || !product.variants.length) {
      return { variant: null, state: 'unavailable' };
    }

    var uniform = uniformVariant(product, colourName);
    if (uniform) return { variant: uniform, state: 'matched' };

    if (sellsByColour(product, colourNames)) {
      return { variant: null, state: 'unavailable' };
    }
    return { variant: firstAvailableVariant(product) || product.variants[0], state: 'colourless' };
  }

  /* Build the list of choices for one add-on.

     Three shapes are supported, in priority order:
       1. a list of one-product-per-colour (the towels)
       2. a single product whose variants carry the colour option
       3. a single product with no colour dimension

     The colour name is taken from a configured colour that appears in
     the product title, so "Mocha Beach Towel" lines up with the Mocha
     seat without any extra setting. */
  function addonOptions(addon, colours) {
    var out = [];
    if (!addon) return out;

    function labelFor(title) {
      var t = norm(title);
      var hit = null;
      colours.forEach(function (c) {
        if (!hit && c.name && t.indexOf(norm(c.name)) !== -1) hit = c;
      });
      return hit;
    }

    var list = addon.colourProducts || [];
    list.forEach(function (prod) {
      if (!prod) return;
      var v = firstAvailableVariant(prod) || (prod.variants || [])[0];
      if (!v) return;
      var hit = labelFor(prod.title);
      out.push({
        label: hit ? hit.name : prod.title,
        colour: hit ? hit.name : null,
        hex: hit ? hit.hex : null,
        image: v.image || prod.image,
        variant: v,
        available: !!v.available
      });
    });
    /* Only take this path if the listed products actually resolved.
       A list of handles that no longer exist used to leave the row
       with no options at all, which read as sold out. */
    if (out.length) return out;

    var product = addon.product;
    if (!product || !(product.variants || []).length) return out;

    var names = colours.map(function (c) { return norm(c.name); }).filter(Boolean);

    function collect(matcher) {
      var found = [];
      product.variants.forEach(function (v) {
        var matched = null;
        (v.options || []).forEach(function (o) {
          if (!matched) matched = matcher(o);
        });
        if (!matched) return;
        var hit = null;
        colours.forEach(function (c) {
          if (!hit && norm(c.name) === norm(matched)) hit = c;
        });
        found.push({
          label: hit ? hit.name : matched,
          colour: hit ? hit.name : matched,
          hex: hit ? hit.hex : null,
          image: v.image || product.image,
          variant: v,
          available: !!v.available
        });
      });
      return found;
    }

    /* Exact option value first, e.g. an option literally named Mocha. */
    var byColour = collect(function (o) {
      return names.indexOf(norm(o)) !== -1 ? o : null;
    });

    /* Then a looser pass, so an option value like "Mocha Cabana" or
       "Cabana - Sage" still lines up with the seat colours. */
    if (!byColour.length) {
      byColour = collect(function (o) {
        var value = norm(o);
        var hit = null;
        names.forEach(function (n) {
          if (!hit && n && value.indexOf(n) !== -1) hit = n;
        });
        return hit;
      });
    }

    if (byColour.length > 1) return byColour;

    var only = firstAvailableVariant(product) || product.variants[0];
    return [{
      label: product.title,
      colour: null,
      hex: null,
      image: only.image || product.image,
      variant: only,
      available: !!only.available
    }];
  }

  /* ============================================================
     Configurator
     ============================================================ */

  function initConfigurator(root) {
    var cfg = parseConfig(root);
    if (!cfg) return;

    var money = function (c) {
      return formatMoney(c, cfg.moneyFormat);
    };

    /* Clamp: a default_pack beyond the number of pack blocks would
       leave no tile selected while pricing silently used another. */
    var packCount = (cfg.packs || []).length;
    var colourCount = (cfg.colours || []).length;
    var state = {
      qty: 1,
      /* The colourway the URL asked for, worked out in Liquid from the
         page's product. Clamped, because a block can be removed in the
         editor between renders. */
      colour: Math.min(Math.max(0, cfg.initialColour || 0), Math.max(0, colourCount - 1)),
      pack: Math.min(Math.max(0, (cfg.defaultPack || 1) - 1), Math.max(0, packCount - 1)),
      addons: {},
      addonColour: {},
      gallery: 0,
      added: false
    };

    /* ---- element refs ---- */
    var el = {
      track: root.querySelector('[data-sspdp-track]'),
      thumbWrap: root.querySelector('[data-sspdp-thumbs]'),
      title: root.querySelector('[data-sspdp-title]'),
      colourName: root.querySelector('[data-sspdp-colour-name]'),
      stars: root.querySelector('[data-sspdp-stars]'),
      swatches: root.querySelectorAll('[data-sspdp-swatch]'),
      packs: root.querySelectorAll('[data-sspdp-pack]'),
      addons: root.querySelectorAll('[data-sspdp-addon]'),
      linePack: root.querySelector('[data-sspdp-line-pack]'),
      lineUnit: root.querySelector('[data-sspdp-line-unit]'),
      qtyValue: root.querySelector('[data-sspdp-qty-value]'),
      qtyLine: root.querySelector('[data-sspdp-qty-line]'),
      qtyTier: root.querySelector('[data-sspdp-qty-tier]'),
      linePackPrice: root.querySelector('[data-sspdp-line-pack-price]'),
      lineAddonCount: root.querySelector('[data-sspdp-line-addon-count]'),
      saving: root.querySelector('[data-sspdp-saving]'),
      savingAmount: root.querySelector('[data-sspdp-saving-amount]'),
      totalWas: root.querySelector('[data-sspdp-total-was]'),
      totalNow: root.querySelector('[data-sspdp-total-now]'),
      shipFill: root.querySelector('[data-sspdp-ship-fill]'),
      shipCaption: root.querySelector('[data-sspdp-ship-caption]'),
      cta: root.querySelector('[data-sspdp-cta]'),
      stock: root.querySelector('[data-sspdp-stock]'),
      eta: root.querySelector('[data-sspdp-eta]'),
      sticky: root.querySelector('[data-sspdp-sticky]'),
      stickyBtn: root.querySelector('[data-sspdp-sticky-btn]'),
      stickyLabel: root.querySelector('[data-sspdp-sticky-label]'),
      stickyTotal: root.querySelector('[data-sspdp-sticky-total]'),
      stickyThumb: root.querySelector('[data-sspdp-sticky-thumb]')
    };

    /* ---- derived pricing ---- */
    var colourNames = (cfg.colours || []).map(function (c) {
      return c.name;
    });

    var addonChoices = (cfg.addons || []).map(function (a) {
      return addonOptions(a, cfg.colours || []);
    });

    function firstAvailableIndex(opts) {
      for (var k = 0; k < opts.length; k++) {
        if (opts[k] && opts[k].available) return k;
      }
      return -1;
    }

    function chosenOption(i) {
      var opts = addonChoices[i] || [];
      if (!opts.length) return null;
      var idx = state.addonColour[i];
      if (idx == null || !opts[idx]) idx = 0;
      /* Never leave a row parked on a colour nobody can buy. A sold
         out default used to disable the whole add-on, which read as
         the product itself being unavailable. Move to the first
         colour that is in stock and remember it, so the swatch that
         shows as chosen is the one that would go in the cart. */
      if (!opts[idx].available) {
        var alt = firstAvailableIndex(opts);
        if (alt !== -1) {
          idx = alt;
          state.addonColour[i] = alt;
        }
      }
      return opts[idx] || null;
    }

    /* Snap each add-on that is set to follow the seat to the matching
       colour, when it offers one. */
    function syncAddonColours() {
      var seat = (cfg.colours[state.colour] || {}).name;
      (cfg.addons || []).forEach(function (addon, i) {
        if (!addon || addon.followSeatColour === false) return;
        var opts = addonChoices[i] || [];
        opts.forEach(function (o, oi) {
          if (o.colour && norm(o.colour) === norm(seat) && o.available) {
            state.addonColour[i] = oi;
          }
        });
      });
    }

    /* Resolve a pack tile for a colour - used by both pricing and the
       per-tile sold-out state so they can never disagree. */
    function packResolution(pack, colour) {
      if (!pack) return { variant: null, state: 'unavailable' };
      if (!pack.product) {
        /* No pack product: add N of the colour product itself, so the
           colour is always honoured. */
        var cv = resolveVariant(colour.product, colour.name, colourNames);
        return { variant: cv.variant, state: cv.state, viaColourProduct: true };
      }
      return resolveVariant(pack.product, colour.name, colourNames);
    }

    /* A selection matches a bundle product when the seat count and the
       exact set of chosen add-ons line up.

       Bundle products carry one option per item - the seat + towel
       bundle reads "Mocha / Sage" for a Mocha seat with a Sage towel -
       so a mixed pair is a real SKU and is matched on its own values
       rather than being ruled out. */
    function comboSignature(seats, keys) {
      return seats + '|' + keys.slice().sort().join(',');
    }

    function matchCombo(seats, selectedKeys, selectedByKey, allKeysKnown, colour) {
      if (!allKeysKnown) return null;
      var wanted = comboSignature(seats, selectedKeys);
      var found = null;
      (cfg.combos || []).forEach(function (c) {
        if (found || !c.product) return;
        var keys = String(c.includes || '')
          .split(',')
          .map(function (k) { return norm(k); })
          .filter(Boolean);
        if (comboSignature(c.seats || 0, keys) !== wanted) return;

        /* Seat colour first, then each add-on's colour in the order
           the bundle lists them. */
        var desired = [colour.name];
        keys.forEach(function (k) {
          desired.push(selectedByKey[k] || colour.name);
        });

        var variant = variantByValues(c.product, desired);
        /* Bundles whose options are all one colour, and single-variant
           bundles, still resolve. */
        if (!variant) variant = uniformVariant(c.product, colour.name);
        if (!variant && (c.product.variants || []).length === 1) {
          variant = c.product.variants[0];
        }
        if (!variant || !variant.available) return;

        found = {
          variant: variant,
          label: c.label || c.product.title,
          colourHonoured: true
        };
      });
      return found;
    }

    function compute() {
      var colour = cfg.colours[state.colour] || cfg.colours[0] || {};
      var pack = cfg.packs[state.pack] || cfg.packs[0] || { seats: 1 };
      var seats = pack.seats || 1;

      /* A stepper stands in for the pack tiles: the shopper says how
         many rather than picking a tile, so this is n of the single
         product and n is whatever the stepper reads. Everything below
         already handles that case - it is the packless branch. */
      if (cfg.quantityMode === 'stepper') {
        seats = Math.max(1, state.qty || 1);
        pack = { seats: seats, product: null, label: '' };
      }

      var colourVariant = (resolveVariant(colour.product, colour.name, colourNames) || {}).variant;
      var packRes = packResolution(pack, colour);
      var packVariant = pack.product ? packRes.variant : null;
      /* Only name the colour when the chosen variant really is that
         colour. A colourless pack SKU must not claim one. */
      var colourHonoured = pack.product ? packRes.state === 'matched' : true;

      var packPrice, packWas, items;
      if (packVariant) {
        packPrice = packVariant.price;
        packWas = packVariant.compare_at && packVariant.compare_at > packVariant.price ? packVariant.compare_at : null;
        /* Fall back to "N x the single seat" as the was-price so the
           saving is honest even without a compare-at price set. */
        if (!packWas && colourVariant && colourVariant.price * seats > packPrice) {
          packWas = colourVariant.price * seats;
        }
        items = [{ id: packVariant.id, quantity: 1 }];
      } else if (!pack.product && colourVariant) {
        packPrice = colourVariant.price * seats;
        packWas = colourVariant.compare_at && colourVariant.compare_at > colourVariant.price
          ? colourVariant.compare_at * seats
          : null;
        items = [{ id: colourVariant.id, quantity: seats }];
      } else {
        packPrice = 0;
        packWas = null;
        items = [];
      }

      var addonNow = 0;
      var addonWas = 0;
      var addonCount = 0;
      var selectedKeys = [];
      var selectedByKey = {};
      var allKeysKnown = true;
      (cfg.addons || []).forEach(function (addon, i) {
        if (!state.addons[i]) return;
        /* Use the colour the shopper picked. Never post an unavailable
           variant - /cart/add.js rejects the whole request if any line
           is unpurchasable, which would block the entire order. */
        var opt = chosenOption(i);
        var v = opt && opt.available ? opt.variant : null;
        if (!v) return;
        addonCount++;

        /* An add-on with no key cannot take part in a bundle match. */
        if (addon.key) {
          selectedKeys.push(norm(addon.key));
          selectedByKey[norm(addon.key)] = opt.colour || colour.name;
        } else {
          allKeysKnown = false;
        }
        var now = addon.bundled != null ? addon.bundled : v.price;
        var was = v.compare_at && v.compare_at > v.price ? v.compare_at : v.price;
        addonNow += now;
        addonWas += Math.max(was, now);
        items.push({ id: v.id, quantity: 1 });
      });

      var total = packPrice + addonNow;
      var wasTotal = (packWas || packPrice) + addonWas;

      /* If a real bundle product covers this exact selection, that one
         product is what goes in the cart, at its own price. The
         itemised figure stays as the was-price so the saving is real. */
      var combo = matchCombo(seats, selectedKeys, selectedByKey, allKeysKnown, colour);
      /* Only swap when the bundle is not dearer than buying the items
         separately - a bundle priced above its parts must never raise
         what the shopper pays. */
      if (combo && total > 0 && combo.variant.price > total) combo = null;
      var bundleLabel = null;
      if (combo) {
        items = [{ id: combo.variant.id, quantity: 1 }];
        if (total > combo.variant.price) wasTotal = total;
        total = combo.variant.price;
        if (wasTotal < total) {
          wasTotal = combo.variant.compare_at && combo.variant.compare_at > total
            ? combo.variant.compare_at
            : total;
        }
        bundleLabel = combo.label;
        colourHonoured = combo.colourHonoured;
      }

      /* Multi-buy tiers. The page shows what the automatic discount in
         admin will take off; Shopify is what actually applies it, so
         the two must be kept in step. Highest qualifying tier wins. */
      var tier = null;
      var nextTier = null;
      if (cfg.quantityMode === 'stepper' && cfg.tiers && cfg.tiers.length) {
        cfg.tiers.forEach(function (t) {
          if (seats >= t.units) {
            if (!tier || t.percent > tier.percent) tier = t;
          } else if (!nextTier || t.units < nextTier.units) {
            nextTier = t;
          }
        });
        if (tier && tier.percent > 0) {
          if (wasTotal < total) wasTotal = total;
          total = Math.round(total * (100 - tier.percent) / 100);
        }
      }

      var saving = Math.max(0, wasTotal - total);
      var available = combo
        ? true
        : pack.product
          ? !!(packVariant && packVariant.available)
          : !!(colourVariant && colourVariant.available);

      return {
        colour: colour,
        pack: pack,
        seats: seats,
        unitPrice: colourVariant ? colourVariant.price : 0,
        tier: tier,
        nextTier: nextTier,
        packPrice: packPrice,
        total: total,
        wasTotal: wasTotal,
        saving: saving,
        items: items,
        available: available,
        /* Resolved even when it cannot be bought: a sold-out colour
           still needs its variant id so the back-in-stock app knows
           what the shopper is waiting for. */
        notifyProduct: (pack.product || colour.product) || null,
        notifyVariantId: (packVariant && packVariant.id) || (colourVariant && colourVariant.id) || null,
        addonCount: addonCount,
        colourHonoured: colourHonoured,
        bundleLabel: bundleLabel,
        isBundle: !!combo,
        ratingPct: cfg.ratingPct || '0%'
      };
    }

    /* Slides and thumbnails are built here rather than in Liquid,
       because how many there are depends on the selected colour.

       The whole set lives in the DOM at once, so moving between
       frames is a scroll, not an image download - that is what made
       switching feel like it took seconds. Sizes come from a srcset
       so a phone fetches a phone-sized file. */
    var panelWidth = cfg.panelWidth || 520;
    var SIZES = '(max-width: 1099px) 100vw, calc(100vw - ' + panelWidth + 'px)';
    var suppressScrollSync = false;

    function buildSlides(images) {
      var track = el.track;
      if (!track) return;
      var signature = images
        .map(function (im) { return im.src; })
        .join('|');
      if (track.getAttribute('data-sig') === signature) return;

      track.innerHTML = '';
      images.forEach(function (im, i) {
        var slide = document.createElement('div');
        slide.className = 'sspdp-hero__slide';

        var img = document.createElement('img');
        img.src = im.src;
        if (im.srcset) {
          img.srcset = im.srcset;
          img.sizes = SIZES;
        }
        img.alt = '';
        img.decoding = 'async';
        /* First frame is the largest paint; the rest wait until the
           shopper scrolls toward them. */
        if (i === 0) {
          img.loading = 'eager';
          img.setAttribute('fetchpriority', 'high');
        } else {
          img.loading = 'lazy';
        }

        slide.appendChild(img);
        track.appendChild(slide);
      });
      track.setAttribute('data-sig', signature);
      goToSlide(state.gallery, false);
    }

    function goToSlide(index, smooth) {
      var track = el.track;
      if (!track || !track.children.length) return;
      var slide = track.children[Math.min(index, track.children.length - 1)];
      if (!slide) return;
      suppressScrollSync = true;
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: smooth === false ? 'auto' : 'smooth'
      });
      window.setTimeout(function () {
        suppressScrollSync = false;
      }, 400);
    }

    function markActiveThumb() {
      var wrap = el.thumbWrap;
      if (!wrap) return;
      Array.prototype.forEach.call(wrap.children, function (btn, i) {
        btn.setAttribute('aria-current', i === state.gallery ? 'true' : 'false');
      });
    }

    function buildThumbs(images) {
      var wrap = el.thumbWrap;
      if (!wrap) return;
      var signature = images
        .map(function (im) { return im.thumb || im.src; })
        .join('|');

      if (wrap.getAttribute('data-sig') !== signature) {
        wrap.innerHTML = '';
        images.forEach(function (im, i) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sspdp-hero__thumb';
          btn.setAttribute('data-sspdp-thumb', '');
          btn.setAttribute('aria-label', (cfg.thumbLabel || 'Show image') + ' ' + (i + 1));

          var img = document.createElement('img');
          img.src = im.thumb || im.src;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          btn.appendChild(img);

          btn.addEventListener('click', function () {
            state.gallery = i;
            markActiveThumb();
            goToSlide(i, true);
          });
          wrap.appendChild(btn);
        });
        wrap.setAttribute('data-sig', signature);
      }
      markActiveThumb();
      wrap.hidden = images.length < 2;
    }

    /* Swiping updates which thumbnail reads as current. */
    if (el.track) {
      var scrollTick = null;
      el.track.addEventListener(
        'scroll',
        function () {
          if (suppressScrollSync || scrollTick) return;
          scrollTick = window.requestAnimationFrame(function () {
            scrollTick = null;
            var track = el.track;
            if (!track.children.length) return;
            var width = track.clientWidth || 1;
            var index = Math.round(track.scrollLeft / width);
            index = Math.max(0, Math.min(index, track.children.length - 1));
            if (index !== state.gallery) {
              state.gallery = index;
              markActiveThumb();
            }
          });
        },
        { passive: true }
      );
    }

    /* ---- render ---- */
    function render() {
      var d = compute();
      var colour = d.colour;

      /* Gallery follows the colour. Each colour is its own product, so
         its product photos are the right detail set - otherwise every
         colour shares one gallery and Sage still shows Mocha. */
      var images;
      var ownPhotos = colour.images || [];
      if (cfg.gallerySource !== 'blocks' && ownPhotos.length) {
        images = ownPhotos;
      } else {
        images = (cfg.gallery || []).slice();
        if (colour.image) {
          images.unshift({ src: colour.image, thumb: colour.thumb || colour.image });
        }
      }
      if (state.gallery >= images.length) state.gallery = 0;
      buildSlides(images);
      buildThumbs(images);

      if (el.stars) el.stars.style.setProperty('--sspdp-rating', d.ratingPct);

      if (el.title && cfg.titleTemplate) {
        el.title.textContent = cfg.titleTemplate.replace('{colour}', colour.name || '');
      }
      if (el.colourName) el.colourName.textContent = colour.name || '';

      /* A colour with nothing in stock says so on its own swatch. It
         is deliberately still selectable - somebody who came for that
         colour should see it sold out rather than be moved quietly
         onto a different one. */
      Array.prototype.forEach.call(el.swatches, function (btn, i) {
        btn.setAttribute('aria-pressed', i === state.colour ? 'true' : 'false');
        var c = cfg.colours[i] || {};
        var res = resolveVariant(c.product, c.name, colourNames) || {};
        var usable = !!(res.variant && res.variant.available);
        btn.classList.toggle('sspdp-swatch--soldout', !usable);
        var flag = btn.querySelector('[data-sspdp-swatch-flag]');
        if (flag) flag.hidden = usable;
      });

      /* Per-seat price on each pack tile, from live variant data. */
      Array.prototype.forEach.call(el.packs, function (btn, i) {
        btn.setAttribute('aria-pressed', i === state.pack ? 'true' : 'false');
        var pack = cfg.packs[i];
        var each = btn.querySelector('[data-sspdp-pack-each]');
        if (!pack || !each) return;
        var seats = pack.seats || 1;
        var res = packResolution(pack, colour);
        var v = res.variant;
        var usable = !!(v && v.available);
        btn.classList.toggle('sspdp-pack--soldout', !usable);
        btn.disabled = !usable;
        if (!v) {
          each.textContent = cfg.soldOutLabel || '';
          return;
        }
        var unit = pack.product ? v.price / seats : v.price;
        each.textContent = money(Math.round(unit)) + (cfg.eachSuffix || '');
      });

      Array.prototype.forEach.call(el.addons, function (row, i) {
        var addon = (cfg.addons || [])[i];
        var opts = addonChoices[i] || [];
        var opt = chosenOption(i);
        var usable = !!(opt && opt.available);

        row.classList.toggle('sspdp-addon--soldout', !usable);
        row.setAttribute('aria-disabled', usable ? 'false' : 'true');
        row.setAttribute('tabindex', usable ? '0' : '-1');
        if (!usable) state.addons[i] = false;
        var on = !!state.addons[i];
        row.setAttribute('data-selected', on ? 'true' : 'false');
        /* aria-checked belongs on the element carrying role="checkbox",
           which is the row itself, not the tick box inside it. */
        row.setAttribute('aria-checked', on ? 'true' : 'false');

        /* Live price for the chosen colour. */
        var priceEl = row.querySelector('[data-sspdp-addon-price]');
        var wasEl = row.querySelector('[data-sspdp-addon-was]');
        var thumbEl = row.querySelector('[data-sspdp-addon-thumb]');
        if (opt) {
          var now = addon && addon.bundled != null ? addon.bundled : opt.variant.price;
          var was = opt.variant.compare_at && opt.variant.compare_at > opt.variant.price
            ? opt.variant.compare_at
            : opt.variant.price;
          if (priceEl) priceEl.textContent = money(now);
          if (wasEl) {
            wasEl.textContent = money(was);
            wasEl.hidden = !(was > now);
          }
          if (thumbEl && opt.image && !row.dataset.lockThumb) thumbEl.src = opt.image;
        }

        /* Colour swatches, only when there is a real choice. */
        var wrap = row.querySelector('[data-sspdp-addon-colours]');
        if (!wrap) return;
        if (opts.length < 2) {
          wrap.innerHTML = '';
          wrap.hidden = true;
          return;
        }
        wrap.hidden = false;
        if (wrap.childElementCount !== opts.length) {
          wrap.innerHTML = '';
          opts.forEach(function (o, oi) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'sspdp-addon__dot';
            dot.setAttribute('data-sspdp-addon-dot', String(oi));
            dot.setAttribute('aria-label', o.label + (o.available ? '' : ' (sold out)'));
            dot.title = o.label;
            if (o.hex) {
              dot.style.background = o.hex;
            } else if (o.image) {
              dot.style.backgroundImage = 'url(' + o.image + ')';
            }
            dot.addEventListener('click', function (e) {
              /* Must not toggle the row underneath. */
              e.stopPropagation();
              if (!o.available) return;
              state.addonColour[i] = oi;
              render();
            });
            wrap.appendChild(dot);
          });
        }
        Array.prototype.forEach.call(wrap.children, function (dot, oi) {
          var o = opts[oi];
          dot.setAttribute('aria-pressed', chosenOption(i) === o ? 'true' : 'false');
          dot.classList.toggle('sspdp-addon__dot--soldout', !o.available);
          dot.disabled = !o.available;
        });
      });

      var baseLabel = d.isBundle ? d.bundleLabel : d.pack.label || '';
      var packLabel = baseLabel + (d.colourHonoured && colour.name ? ' · ' + colour.name : '');
      if (el.linePack) el.linePack.textContent = packLabel;
      if (el.linePackPrice) el.linePackPrice.textContent = money(d.packPrice);
      if (el.lineUnit) el.lineUnit.textContent = money(d.unitPrice);
      if (el.qtyValue) el.qtyValue.textContent = String(d.seats);
      if (el.qtyLine) {
        var word = d.seats === 1 ? (cfg.qtySingular || 'item') : (cfg.qtyPlural || 'items');
        el.qtyLine.textContent = d.seats + ' ' + word;
      }
      if (el.qtyTier) {
        /* Say what the next tier is worth while it is still reachable,
           and what was earned once it is. Silent when there are none. */
        var line = '';
        if (d.nextTier) {
          line = (cfg.tierNextTemplate || '{n} more for {label}')
            .replace('{n}', String(Math.max(1, d.nextTier.units - d.seats)))
            .replace('{label}', d.nextTier.label || '');
        } else if (d.tier) {
          line = d.tier.label || '';
        }
        el.qtyTier.textContent = line;
        el.qtyTier.hidden = line === '';
      }
      if (el.lineAddonCount) el.lineAddonCount.textContent = String(d.addonCount);

      if (el.saving) el.saving.hidden = d.saving <= 0;
      if (el.savingAmount) el.savingAmount.textContent = '−' + money(d.saving);
      if (el.totalWas) {
        el.totalWas.hidden = d.saving <= 0;
        el.totalWas.textContent = money(d.wasTotal);
      }
      if (el.totalNow) el.totalNow.textContent = money(d.total);

      var threshold = cfg.freeShipThreshold || 0;
      if (el.shipFill) {
        var pct = threshold > 0 ? Math.min(100, Math.round((d.total / threshold) * 100)) : 100;
        el.shipFill.style.width = pct + '%';
      }
      if (el.shipCaption) {
        el.shipCaption.textContent = (threshold > 0 && d.total < threshold)
          ? (cfg.shipProgress || '').replace('{remaining}', money(threshold - d.total)).replace('{threshold}', money(threshold))
          : (cfg.shipUnlocked || '');
      }

      var soldOut = !d.available && !state.added;
      /* Only offer a signup when there is something concrete to sign up
         for. Without a variant the app has nothing to register. */
      var canNotify = soldOut && !!(d.notifyProduct && d.notifyVariantId);
      var notifyJson = canNotify ? JSON.stringify(d.notifyProduct) : '';
      var soldOutText = canNotify
        ? (cfg.notifyLabel || 'Notify me')
        : (cfg.soldOutLabel || 'Sold out');

      var ctaLabel = state.added
        ? cfg.addedLabel
        : d.available
          ? (cfg.ctaLabel || '').replace('{total}', money(d.total))
          : soldOutText;

      var stickyLabel = state.added
        ? cfg.addedLabel
        : d.available
          ? (cfg.stickyCta || cfg.ctaLabel || '').replace('{total}', money(d.total))
          : soldOutText;

      /* Both buttons take the same treatment. The sticky one used to
         build an Add to cart string whatever the stock said and then
         simply get disabled, so it sat there in marigold reading Add to
         cart on a colour nobody could buy. */
      function paintCta(btn, label) {
        if (!btn) return;
        btn.textContent = label;
        btn.setAttribute('data-added', state.added ? 'true' : 'false');
        /* Disabled only when there is nothing to do. A button offering
           a signup has to stay clickable. */
        btn.disabled = soldOut && !canNotify;
        btn.classList.toggle('is-notify', canNotify);
        if (canNotify) {
          btn.setAttribute('data-ss-notify', '');
          btn.setAttribute('data-ss-notify-variant', String(d.notifyVariantId));
          btn.setAttribute('data-ss-notify-product', notifyJson);
        } else {
          btn.removeAttribute('data-ss-notify');
          btn.removeAttribute('data-ss-notify-variant');
          btn.removeAttribute('data-ss-notify-product');
        }
      }

      paintCta(el.cta, ctaLabel);
      paintCta(el.stickyBtn, stickyLabel);

      /* The stock line was typed text, so it read IN STOCK over a
         sold-out colour. It follows the chosen colour now. */
      if (el.stock) {
        var stockText = d.available ? (cfg.stockLabel || '') : (cfg.stockSoldOut || cfg.soldOutLabel || '');
        el.stock.textContent = stockText;
        el.stock.hidden = stockText === '';
        el.stock.classList.toggle('is-soldout', !d.available);
      }
      if (el.stickyLabel) el.stickyLabel.textContent = packLabel;
      if (el.stickyTotal) el.stickyTotal.textContent = money(d.total);
      if (el.stickyThumb && colour.image) el.stickyThumb.src = colour.image;

      root.dataset.sspdpTotal = String(d.total);
      return d;
    }

    /* ---- events ---- */
    Array.prototype.forEach.call(el.swatches, function (btn, i) {
      btn.addEventListener('click', function () {
        if (state.colour === i) return;
        state.colour = i;
        state.gallery = 0;
        syncAddonColours();
        render();
        goToSlide(0, false);
      });
    });

    Array.prototype.forEach.call(el.packs, function (btn, i) {
      btn.addEventListener('click', function () {
        state.pack = i;
        render();
      });
    });

    /* The stepper, when this product is bought in any number rather
       than in fixed packs. Clamped so a shopper cannot reach zero or
       type their way to a thousand towels. */
    Array.prototype.forEach.call(root.querySelectorAll('[data-sspdp-qty-step]'), function (btn) {
      btn.addEventListener('click', function () {
        var by = parseInt(btn.getAttribute('data-sspdp-qty-step'), 10) || 0;
        var next = Math.max(1, Math.min(99, (state.qty || 1) + by));
        if (next === state.qty) return;
        state.qty = next;
        render();
      });
    });

    Array.prototype.forEach.call(el.addons, function (row, i) {
      row.addEventListener('click', function () {
        if (row.getAttribute('aria-disabled') === 'true') return;
        state.addons[i] = !state.addons[i];
        render();
      });
      row.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          row.click();
        }
      });
    });

    /* ---- add to cart ---- */
    var addedTimer = null;
    function addToCart() {
      var d = compute();
      if (!d.items.length || !d.available) return;

      var buttons = [el.cta, el.stickyBtn].filter(Boolean);
      buttons.forEach(function (b) { b.disabled = true; });

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: d.items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function () {
          /* Apply the real bundle discount so the cart total matches
             the receipt - but only when this configuration actually
             shows a saving, so a single seat never picks up a bundle
             discount it did not earn. */
          if (cfg.discountCode && d.saving > 0 && !d.isBundle) {
            return fetch('/discount/' + encodeURIComponent(cfg.discountCode) + '?redirect=/cart.js', {
              credentials: 'same-origin'
            }).catch(function () {});
          }
        })
        .then(function () {
          state.added = true;
          buttons.forEach(function (b) { b.disabled = false; });
          render();
          /* Carry a detail payload - theme listeners read it. */
          document.dispatchEvent(
            new CustomEvent('cart:update', { bubbles: true, detail: { source: 'ss-pdp' } })
          );
          if (cfg.openDrawer) {
            var drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          }
          clearTimeout(addedTimer);
          addedTimer = setTimeout(function () {
            state.added = false;
            render();
          }, 2400);
        })
        .catch(function () {
          buttons.forEach(function (b) { b.disabled = false; });
          render();
        });
    }

    if (el.cta) el.cta.addEventListener('click', addToCart);
    if (el.stickyBtn) el.stickyBtn.addEventListener('click', addToCart);

    /* ---- delivery estimate ---- */
    if (el.eta && cfg.etaTemplate) {
      el.eta.textContent = cfg.etaTemplate
        .replace('{from}', businessDay(cfg.etaMin || 2))
        .replace('{to}', businessDay(cfg.etaMax || 5));
    }

    /* ---- sticky visibility ----
       Visible whenever the real Add to cart is off screen, including
       before the shopper has scrolled down to it. Previously it only
       appeared after scrolling past, which left most of the page with
       no way to buy.

       While it is showing, the page gets bottom padding equal to the
       bar's height so it never covers the last of the content. */
    function setStickyVisible(on) {
      if (!el.sticky) return;
      el.sticky.classList.toggle('is-visible', on);
      document.body.classList.toggle('sspdp-has-sticky', on);
      if (on) {
        document.body.style.setProperty('--sspdp-sticky-h', el.sticky.offsetHeight + 'px');
      }
    }

    if (el.sticky && el.cta && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            setStickyVisible(!entry.isIntersecting);
          });
        },
        { rootMargin: '0px', threshold: 0 }
      );
      observer.observe(el.cta);
    }

    /* Fetch the first frame of every other colour once the page is
       idle, so tapping a swatch shows its photo immediately instead
       of waiting on a cold request. */
    function preloadOtherColours() {
      (cfg.colours || []).forEach(function (c, i) {
        if (i === state.colour) return;
        var first = (c.images || [])[0];
        var src = first ? first.src : c.image;
        if (!src) return;
        var img = new Image();
        if (first && first.srcset) {
          img.sizes = SIZES;
          img.srcset = first.srcset;
        }
        img.src = src;
      });
    }

    syncAddonColours();
    render();

    if (window.requestIdleCallback) {
      window.requestIdleCallback(preloadOtherColours, { timeout: 3000 });
    } else {
      window.setTimeout(preloadOtherColours, 1500);
    }
  }

  /* ============================================================
     Recline demo
     ============================================================ */

  function initRecline(root) {
    var chips = root.querySelectorAll('[data-sspdp-angle]');
    var frames = root.querySelectorAll('[data-sspdp-recline-frame]');
    var back = root.querySelector('[data-sspdp-back]');
    var num = root.querySelector('[data-sspdp-angle-num]');
    var label = root.querySelector('[data-sspdp-angle-label]');
    var desc = root.querySelector('[data-sspdp-angle-desc]');

    function select(chip) {
      Array.prototype.forEach.call(chips, function (c) {
        c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
      });

      /* Cross-fade to this position's frame. */
      var index = chip.getAttribute('data-index');
      Array.prototype.forEach.call(frames, function (f) {
        f.classList.toggle('is-active', f.getAttribute('data-index') === index);
      });

      /* Diagram fallback: negative so the backrest rises rather than
         swinging below the seat pan. */
      if (back) back.style.transform = 'rotate(-' + (chip.getAttribute('data-angle') || 0) + 'deg)';
      if (num) num.textContent = chip.getAttribute('data-num') || '';
      if (label) label.textContent = chip.getAttribute('data-label') || '';
      if (desc) desc.textContent = chip.getAttribute('data-desc') || '';
    }

    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('click', function () {
        select(chip);
      });
    });

    var initial = root.querySelector('[data-sspdp-angle][aria-pressed="true"]') || chips[0];
    if (initial) select(initial);
  }

  /* ============================================================
     FAQ accordions
     ============================================================ */

  function initFaq(root) {
    root.querySelectorAll('[data-sspdp-faq]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.sspdp-faq');
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        var mark = btn.querySelector('.sspdp-faq__mark');
        if (mark) mark.textContent = open ? '−' : '+';
      });
    });
  }

  /* ============================================================
     Bundle add buttons
     ============================================================ */

  function initBundles(root) {
    root.querySelectorAll('[data-sspdp-bundle-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-variant-id');
        if (!id) return;
        var label = btn.textContent;
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            btn.textContent = btn.getAttribute('data-added-label') || label;
            btn.setAttribute('data-added', 'true');
            /* Carry a detail payload - theme listeners read it. */
          document.dispatchEvent(
            new CustomEvent('cart:update', { bubbles: true, detail: { source: 'ss-pdp' } })
          );
            setTimeout(function () {
              btn.textContent = label;
              btn.setAttribute('data-added', 'false');
              btn.disabled = false;
            }, 2400);
          })
          .catch(function () {
            btn.disabled = false;
          });
      });
    });
  }

  /* ============================================================
     Judge.me summary

     The designed summary - score, stars, count, histogram - is filled
     from the data Judge.me ships alongside its widget, so those
     numbers stay right without anyone editing the theme. Judge.me's
     own header is hidden in CSS; this is what replaces it.

     Everything here is best-effort. If the data never turns up, the
     values typed into the section settings stay on screen, which is
     the same thing a shopper would have seen before.
     ============================================================ */

  function initJudgeMe(root) {
    var widget = root.querySelector('#judgeme_product_reviews, .jdgm-review-widget');
    if (!widget) return;

    var productId = widget.getAttribute('data-id') || widget.getAttribute('data-product-id');

    /* Our button opens Judge.me's review form rather than jumping to
       the widget, which is all the bare anchor could do. */
    var write = root.querySelector('[data-sspdp-jm-write]');
    if (write) {
      write.addEventListener('click', function (e) {
        var btn = widget.querySelector('[data-testid="write-review-button"]');
        if (!btn) return;
        e.preventDefault();
        btn.click();
      });
    }

    function summary() {
      var data = window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget;
      if (!data || !productId) return null;
      return data[productId] || data[Number(productId)] || null;
    }

    function paint(d) {
      var avg = parseFloat(d.average_rating);
      var count = parseInt(d.number_of_reviews, 10);

      if (!isNaN(avg)) {
        var shown = (Math.round(avg * 10) / 10).toFixed(1);
        var scoreEl = root.querySelector('[data-sspdp-jm-score]');
        if (scoreEl) scoreEl.textContent = shown;

        var starsEl = root.querySelector('[data-sspdp-jm-stars]');
        if (starsEl) {
          starsEl.style.setProperty('--sspdp-rating', (avg / 5) * 100 + '%');
          starsEl.setAttribute('aria-label', shown + ' out of 5');
        }
      }

      var countEl = root.querySelector('[data-sspdp-jm-count]');
      if (countEl && !isNaN(count)) {
        countEl.textContent = count + ' review' + (count === 1 ? '' : 's');
      }

      /* Rows are matched on their star value, so reordering or
         removing a row in the editor still lands the right figures. */
      (d.histogram || []).forEach(function (row) {
        var el = root.querySelector('[data-sspdp-jm-hist="' + row.rating + '"]');
        if (!el) return;
        var fill = el.querySelector('[data-sspdp-jm-hist-fill]');
        var num = el.querySelector('[data-sspdp-jm-hist-count]');
        if (fill) fill.style.width = (row.percentage || 0) + '%';
        if (num) num.textContent = row.frequency;
      });
    }

    var found = summary();
    if (found) {
      paint(found);
      return;
    }

    /* The widget mounts asynchronously, so give the data a moment to
       appear rather than giving up on the first miss. */
    var waited = 0;
    var timer = setInterval(function () {
      var d = summary();
      waited += 200;
      if (d) {
        clearInterval(timer);
        paint(d);
      } else if (waited >= 4000) {
        clearInterval(timer);
      }
    }, 200);
  }

  /* ============================================================
     Boot
     ============================================================ */

  /* The video band autoplays; honour a reduced-motion preference by
     pausing it on the poster frame. */
  /* ---------- image switch ----------
     Chips move the image and the copy together. Everything the panel
     needs travels on the chip itself, so the section needs no second
     source of truth and no fetch when a shopper switches. */
  function initSwitch(root) {
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-sspdp-switch-chip]'));
    if (!chips.length) return;

    var imgs = Array.prototype.slice.call(root.querySelectorAll('[data-sspdp-switch-img]'));
    var eyebrow = root.querySelector('[data-sspdp-switch-eyebrow]');
    var label = root.querySelector('[data-sspdp-switch-label]');
    var desc = root.querySelector('[data-sspdp-switch-desc]');

    function show(index) {
      var want = String(index);
      chips.forEach(function (c) {
        c.setAttribute('aria-pressed', c.getAttribute('data-index') === want ? 'true' : 'false');
      });
      imgs.forEach(function (img) {
        var on = img.getAttribute('data-index') === want;
        img.setAttribute('data-active', on ? 'true' : 'false');
        if (on) img.removeAttribute('aria-hidden');
        else img.setAttribute('aria-hidden', 'true');
      });
      var chip = chips.filter(function (c) { return c.getAttribute('data-index') === want; })[0];
      if (!chip) return;
      if (eyebrow) {
        var e = chip.getAttribute('data-eyebrow') || '';
        eyebrow.textContent = e;
        eyebrow.hidden = e === '';
      }
      if (label) label.textContent = chip.getAttribute('data-label') || '';
      if (desc) desc.textContent = chip.getAttribute('data-desc') || '';
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        show(parseInt(chip.getAttribute('data-index'), 10) || 0);
      });
    });
  }

  /* ---------- numbered steps ----------
     One click moves the row, the bar, the image, the description and
     the caption. Everything the panel needs travels on the row. */
  function initSteps(root) {
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-sspdp-step]'));
    if (!rows.length) return;

    var bars = Array.prototype.slice.call(root.querySelectorAll('[data-sspdp-step-bar]'));
    var frames = Array.prototype.slice.call(root.querySelectorAll('[data-sspdp-step-frame]'));
    var desc = root.querySelector('[data-sspdp-step-desc]');
    var readout = root.querySelector('[data-sspdp-step-readout]');
    var capt = root.querySelector('[data-sspdp-step-capt]');

    function show(index) {
      var want = String(index);
      rows.forEach(function (r) {
        r.setAttribute('aria-pressed', r.getAttribute('data-index') === want ? 'true' : 'false');
      });
      bars.forEach(function (b) {
        b.setAttribute('data-active', b.getAttribute('data-index') === want ? 'true' : 'false');
      });
      frames.forEach(function (f) {
        f.setAttribute('data-active', f.getAttribute('data-index') === want ? 'true' : 'false');
      });
      var row = rows.filter(function (r) { return r.getAttribute('data-index') === want; })[0];
      if (!row) return;
      if (desc) desc.textContent = row.getAttribute('data-desc') || '';
      if (readout) readout.textContent = row.getAttribute('data-readout') || '';
      if (capt) capt.textContent = row.getAttribute('data-label') || '';
    }

    rows.forEach(function (row) {
      row.addEventListener('click', function () {
        show(parseInt(row.getAttribute('data-index'), 10) || 0);
      });
    });
  }

  function initReducedMotion(root) {
    if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    root.querySelectorAll('.sspdp-video__media').forEach(function (v) {
      if (typeof v.pause !== 'function') return;
      v.autoplay = false;
      v.removeAttribute('autoplay');
      v.pause();
    });
  }

  function init(root) {
    if (!root || root.dataset.sspdpInit === '1') return;
    root.dataset.sspdpInit = '1';
    initReducedMotion(root);
    if (root.querySelector('[data-sspdp-config]')) initConfigurator(root);
    if (root.querySelector('[data-sspdp-angle]')) initRecline(root);
    if (root.querySelector('[data-sspdp-switch-chip]')) initSwitch(root);
    if (root.querySelector('[data-sspdp-step]')) initSteps(root);
    if (root.querySelector('[data-sspdp-faq]')) initFaq(root);
    if (root.querySelector('[data-sspdp-bundle-add]')) initBundles(root);
    if (root.querySelector('[data-sspdp-jm]')) initJudgeMe(root);
  }

  function boot() {
    document.querySelectorAll('.sspdp').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    e.target.querySelectorAll('.sspdp').forEach(init);
  });
})();
